import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSalonScopedFetch } from "../hooks/useSalonScopedFetch";
import { useNavigate } from "react-router-dom";
import { useValueChurnProbe } from "../utils/loopDebug";
import { useAuth } from "./AuthContext";
import { useApp } from "./AppContext";
import { useFeatureAccess } from "./FeatureAccessContext";
import {
  fetchCurrentSalonRecord,
  resolveCurrentLocation,
} from "../utils/currentLocation";

const SalonContext = createContext(null);

export function SalonProvider({ children }) {
  const navigate = useNavigate();
  const { isAuthenticated, currentUser: currentCognitoUser } = useAuth();
  const {
    getSalons,
    getSalonByUuid,
    getSalonStaff,
    getServices,
    getOnboardingProgress,
    getPaymentTerminals,
  } = useApp();
  // Get onboarding state from /access endpoint (primary source)
  const {
    access,
    isLoading: isAccessLoading,
    error: accessError,
    refreshAccess,
  } = useFeatureAccess();

  const [businessInfo, setBusinessInfo] = useState();
  const [paymentTerminals, setPaymentTerminals] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [services, setServices] = useState(null);
  const [serviceTypeOptions, setServiceTypeOptions] = useState();
  const [isMissingSalon, setIsMissingSalon] = useState(null);
  const [onboardingStage, setOnboardingStage] = useState();
  // onboardingProgress is now derived from /access endpoint when available
  // Falls back to local state if access is not loaded
  const [localOnboardingProgress, setLocalOnboardingProgress] = useState(null);

  // Derive onboarding progress from access data (primary source).
  // Memoized (loop fix): the old inline object was a NEW identity every
  // render and sat in downstream effect deps (services fetch, nav
  // visibility), refiring them on every provider re-render.
  const accessOnboarding = access?.onboarding;
  const onboardingProgress = useMemo(
    () =>
      accessOnboarding
        ? {
            initialOnboarding: accessOnboarding.initial_onboarding,
            progressiveEmployees: accessOnboarding.progressive_employees,
            progressiveServices: accessOnboarding.progressive_services,
            progressivePayments: accessOnboarding.progressive_payments,
          }
        : localOnboardingProgress,
    [accessOnboarding, localOnboardingProgress],
  );

  // Setter that updates local state (for backwards compatibility)
  const setOnboardingProgress = setLocalOnboardingProgress;

  // Multi-location (PR A / #399): a staff member assigned to more than one
  // location picks/switches which single location they're viewing. Derived
  // from /access; empty list / null selection when the backend
  // MULTI_LOCATION_READS_ENABLED flag is OFF, so the app stays single-location.
  // Memoized so consumers don't see a fresh [] identity every render.
  const accessAvailableLocations = access?.available_locations;
  const availableLocations = useMemo(
    () => accessAvailableLocations ?? [],
    [accessAvailableLocations],
  );
  // selectedSalonId is this tab's own location as echoed by /access (its
  // X-Business-Location-Id — utils/actingLocation), or the remembered landing
  // location for a brand-new tab. The switcher can optimistically override
  // it for the brief highlight before the full page reload via setSelectedSalonId.
  const [selectedSalonIdOverride, setSelectedSalonId] = useState(null);
  const selectedSalonId =
    selectedSalonIdOverride ?? access?.selected_salon_id ?? null;

  // Full-page location-picker visibility for the manual-switch context (NavBar
  // building icon). The first-sign-in context is derived at the App root from
  // availableLocations.length > 1 && selectedSalonId == null.
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const openLocationPicker = useCallback(
    () => setIsLocationPickerOpen(true),
    [],
  );
  const closeLocationPicker = useCallback(
    () => setIsLocationPickerOpen(false),
    [],
  );

  const [currentUser, setCurrentUser] = useState();
  // Loading state specifically for routing decisions (salon + employees)
  const [isRoutingDataLoading, setIsRoutingDataLoading] = useState(false);

  // Derive user role from access.user_level immediately (no waiting for staff fetch)
  // This allows routing decisions to happen faster
  const userRoleFromAccess = access?.user_level || null;

  // Set currentUser from employees list when available.
  // Only call setEmployees when isSelf is not yet set (avoid new array reference every run → infinite loop).
  //
  // MAX-DEPTH BUG (found live 2026-07-23, multi-location second location):
  // when the salon has NO staff rows, `getSalonStaff` returns [] and
  // `[].some(...)` is ALWAYS false — the old guard then called
  // `setEmployees([].map(...))`, a fresh empty array every pass, which
  // changed the `employees` dep identity and synchronously refired this
  // effect forever ("Maximum update depth exceeded"; componentStack bottomed
  // in NavBar because the paired setCurrentUser below also minted a new
  // object per pass, re-rendering every useSalon consumer). An empty list
  // has nothing to annotate — treat it as already-annotated. setCurrentUser
  // now also bails when the derived user is unchanged, so no consumer
  // re-renders on a no-op pass. (Updaters are pure — StrictMode-safe.)
  useEffect(() => {
    if (employees && currentCognitoUser) {
      const foundUser = employees?.find(
        (emp) => emp.id === currentCognitoUser.userId,
      );
      if (foundUser) {
        setCurrentUser(foundUser);
      } else if (userRoleFromAccess) {
        setCurrentUser((prev) =>
          prev &&
          prev.id === currentCognitoUser.userId &&
          prev.role === userRoleFromAccess
            ? prev
            : { id: currentCognitoUser.userId, role: userRoleFromAccess },
        );
      }
      const alreadyHasIsSelf =
        employees.length === 0 || employees.some((emp) => "isSelf" in emp);
      if (!alreadyHasIsSelf) {
        setEmployees(
          employees.map((emp) => ({
            ...emp,
            isSelf: emp.id === currentCognitoUser.userId,
          })),
        );
      }
    } else if (!employees && userRoleFromAccess && currentCognitoUser) {
      setCurrentUser((prev) =>
        prev &&
        prev.id === currentCognitoUser.userId &&
        prev.role === userRoleFromAccess
          ? prev
          : { id: currentCognitoUser.userId, role: userRoleFromAccess },
      );
    }
  }, [currentCognitoUser, employees, userRoleFromAccess]);

  useEffect(() => {
    if (services) {
      setServiceTypeOptions([
        ...new Set(services.map((service) => service.service_type)),
      ]);
    }
  }, [services]);

  const hasNoBusinessHours = (salon) => {
    return !(
      salon.monday_open_time ||
      salon.monday_end_time ||
      salon.tuesday_open_time ||
      salon.tuesday_end_time ||
      salon.wednesday_open_time ||
      salon.wednesday_end_time ||
      salon.thursday_open_time ||
      salon.thursday_end_time ||
      salon.friday_open_time ||
      salon.friday_end_time ||
      salon.saturday_open_time ||
      salon.saturday_end_time ||
      salon.sunday_open_time ||
      salon.sunday_end_time
    );
  };

  // Helper to check if progressive onboarding is complete
  const isProgressiveComplete = (progress) => {
    return (
      progress?.progressiveEmployees === "complete" &&
      progress?.progressiveServices === "complete" &&
      progress?.progressivePayments === "complete"
    );
  };

  // Helper to check if user needs onboarding
  const needsOnboarding = (progress) => {
    if (!progress) return true;

    // If initial onboarding is not complete, needs onboarding
    if (
      progress.initialOnboarding !== "complete" &&
      progress.initialOnboarding !== null
    ) {
      return true;
    }

    // If initial onboarding is complete but progressive is not, show dashboard
    if (
      progress.initialOnboarding === "complete" &&
      !isProgressiveComplete(progress)
    ) {
      return true;
    }

    return false;
  };

  // Initial data fetch on login. Waits for /access to settle first: when the
  // multi-location flag is ON, the current salon MUST be resolved from
  // available_locations + selected_salon_id (utils/currentLocation.ts).
  // Plain GET /business-locations follows the legacy salon_staff link, which does NOT
  // track last_used_salon_id — after a location switch it keeps returning the
  // pre-switch salon on every boot (even hard refreshes), which put every
  // uuid-scoped fetch (staff/services/clients/products/settings) in the old
  // location while server-scoped fetches showed the new one. One fetch per
  // resolved salon identity (fetchedSalonKeyRef) so background /access
  // refreshes don't re-trigger the whole boot fetch.
  const fetchedSalonKeyRef = useRef(null);
  // "Settled" = the /access fetch finished (loaded or errored). isLoading
  // starts false BEFORE the fetch kicks off (it's effect-driven in
  // FeatureAccessContext, and this child effect runs first), so gating on
  // isLoading alone would still fire a legacy fetch against the old salon.
  const accessSettled =
    !isAccessLoading && (access != null || accessError != null);
  // The effect below must key on the RESOLVED salon identity (a primitive),
  // not the `access` object: every /access refetch mints a new object even
  // when nothing changed, and having it in the deps re-ran the boot fetch on
  // every refresh churn (live capture: SalonContext.js setBusinessInfo firing
  // repeatedly). The latest access object is read through a ref instead.
  const accessRef = useRef(access);
  accessRef.current = access;
  const resolvedLocation = resolveCurrentLocation(access);
  const resolvedSalonKey = resolvedLocation
    ? resolvedLocation.salon_uuid
    : "legacy";
  useEffect(() => {
    if (!isAuthenticated || !accessSettled) return;
    const salonKey = resolvedSalonKey;
    if (fetchedSalonKeyRef.current === salonKey) return;
    fetchedSalonKeyRef.current = salonKey;
    const access = accessRef.current;

    const fetchBusinessInfo = async () => {
      try {
        setIsRoutingDataLoading(true);

        // Fetch salon info (onboarding is handled by separate effect or /access)
        let salon = null;
        try {
          salon = await fetchCurrentSalonRecord({
            access,
            getSalons,
            getSalonByUuid,
          });
          if (salon) {
            setBusinessInfo(salon);
            setIsMissingSalon(false);
          } else {
            setIsMissingSalon(true);
          }
        } catch (salonError) {
          setIsMissingSalon(true);
        }

        // If salon exists, fetch employees
        if (salon) {
          try {
            const fetchedStaff = await getSalonStaff(salon.id);
            setEmployees(fetchedStaff || []);
            // Update currentUser if we found the user in staff list
            if (fetchedStaff && currentCognitoUser) {
              const foundUser = fetchedStaff.find(
                (emp) => emp.id === currentCognitoUser.userId,
              );
              if (foundUser) {
                setCurrentUser(foundUser);
              }
            }
          } catch (err) {
            console.error("Error fetching staff:", err);
          }
        }
      } catch (error) {
        console.error("Error in fetchBusinessInfo:", error);
      } finally {
        setIsRoutingDataLoading(false);
      }
    };

    fetchBusinessInfo();
  }, [isAuthenticated, accessSettled, resolvedSalonKey]);

  // GET /business-locations does not include business_vertical or staffing_model; they are on GET /access.
  // Merge them into businessInfo so Studio settings and other consumers match the salon taxonomy.
  useEffect(() => {
    if (!access || !businessInfo?.id) return;
    setBusinessInfo((prev) => {
      if (!prev?.id) return prev;
      const next = { ...prev };
      let changed = false;

      if (
        access.business_vertical != null &&
        access.business_vertical !== prev.business_vertical
      ) {
        next.business_vertical = access.business_vertical;
        changed = true;
      }

      if (
        access.staffing_model != null &&
        access.staffing_model !== prev.staffing_model
      ) {
        next.staffing_model = access.staffing_model;
        changed = true;
      }

      return changed ? next : prev;
    });
    // Depend on businessInfo (object — must re-merge whenever it is replaced,
    // same id) plus the two PRIMITIVE access fields actually merged. The
    // whole `access` object is deliberately NOT a dep: every /access refetch
    // mints a new object and would re-run this on every refresh churn.
  }, [access?.business_vertical, access?.staffing_model, businessInfo]);

  // Onboarding fallback: only fetch via getOnboardingProgress when /access is
  // not yet available. Guarded to ONE attempt per mount (loop fix): the old
  // effect had getOnboardingProgress — an AppContext method whose identity
  // churned on every AppProvider render — in its deps, so it refired the
  // fetch (and setLocalOnboardingProgress, a fresh object each time) over and
  // over (live capture: SalonContext getOnboardingProgress().then(...)
  // repeating). One attempt is correct: this is a fallback for the brief
  // window before /access carries onboarding.
  // Flag-ON = /access advertises available_locations (mirrors
  // AccountCreationPage's load gating). Staff-keyed (no-salon-arg)
  // onboarding fallbacks are ONLY legal when the flag is OFF: the staff row
  // is the ORIGINAL salon's and can point at a different location.
  const multiLocationOn = (access?.available_locations?.length ?? 0) > 0;
  useSalonScopedFetch(
    "onboarding-progress-fallback",
    () =>
      getOnboardingProgress().then((progress) => {
        setLocalOnboardingProgress(progress);
      }),
    {
      // accessSettled is REQUIRED: at boot `access` is still null when
      // isAuthenticated flips true, so gating on !multiLocationOn alone let
      // the staff-keyed GET /onboardings fire once before /access revealed
      // available_locations — exactly the cross-location channel this
      // fallback is meant to be barred from when the flag is ON.
      enabled: Boolean(
        isAuthenticated &&
        accessSettled &&
        !access?.onboarding &&
        !multiLocationOn,
      ),
      tag: "onboarding-fallback",
    },
  );

  // Clear state on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setBusinessInfo(undefined);
      setEmployees(null);
      setServices(null);
      setOnboardingProgress(null);
      setCurrentUser(undefined);
      setIsMissingSalon(null);
      fetchedSalonKeyRef.current = null;
      // Multi-location switcher state: a stale optimistic override or an
      // open picker must not leak into the next sign-in in the same tab.
      setSelectedSalonId(null);
      setIsLocationPickerOpen(false);
    }
  }, [isAuthenticated]);

  // Skip fetching services ONLY during initial onboarding (wizard flow).
  // Derived OUTSIDE the effect so the effect can depend on this primitive:
  // `onboardingProgress` is a new inline object every render, and having it in
  // the deps refired getServices on EVERY provider re-render while `services`
  // was still null — unbounded when the backend 403s the fetch (multi-location
  // second location). Primitive deps make a failed fetch stay failed until a
  // real change (salon id / onboarding stage / services reset).
  const isInitialOnboardingInProgress =
    onboardingProgress?.initialOnboarding === "inprogress" ||
    onboardingProgress?.initialOnboarding === null ||
    onboardingProgress?.initialOnboarding === "null";

  // Fetch services when businessInfo is available and initial onboarding is complete
  useEffect(() => {
    // Fetch services if:
    // - We have business info AND
    // - Initial onboarding is NOT in progress (either complete or we're past it)
    if (businessInfo?.id && !isInitialOnboardingInProgress && !services) {
      getServices(businessInfo.id)
        .then((servicesData) => {
          setServices(servicesData || []);
          if (
            !onboardingStage &&
            (!servicesData || servicesData.length === 0)
          ) {
            setOnboardingStage("add-services");
          }
        })
        .catch((err) => {
          console.error("Error fetching services:", err);
        });
    }
  }, [businessInfo?.id, isInitialOnboardingInProgress, services]);

  // Function to manually refresh employees list
  const refreshEmployees = async () => {
    if (!businessInfo?.id) return null;
    try {
      const staff = await getSalonStaff(businessInfo.id);
      setEmployees(staff || []);
      return staff;
    } catch (error) {
      console.error("Error refreshing employees:", error);
      return null;
    }
  };

  // Payment terminals (up to 3 per location). Devices page + checkout read this;
  // callers refresh after add/remove/rename.
  const refreshPaymentTerminals = async () => {
    if (!businessInfo?.id) return null;
    try {
      const response = await getPaymentTerminals(businessInfo.id);
      const terminals = response?.terminals || [];
      setPaymentTerminals(terminals);
      return terminals;
    } catch (error) {
      console.error("Error refreshing payment terminals:", error);
      // Resolve to an empty list rather than leaving state at null — Devices.js
      // gates its whole render on `paymentTerminals === null` and would otherwise
      // spin forever on a failed initial fetch (network blip, 401/500). Checkout
      // is unaffected (it reads `paymentTerminals ?? []`).
      setPaymentTerminals([]);
      return null;
    }
  };

  // Load terminals whenever the acting location changes (incl. first load and
  // location switches); reset while there is no location. (deps intentionally
  // limited to the location id; the exhaustive-deps rule is not loaded by this
  // CRA base ESLint config, so a disable directive for it would fail the build.)
  useEffect(() => {
    if (businessInfo?.id) {
      refreshPaymentTerminals();
    } else {
      setPaymentTerminals(null);
    }
  }, [businessInfo?.id]);

  // Function to manually refresh services list
  const refreshServices = async () => {
    if (!businessInfo?.id) return null;
    try {
      const servicesData = await getServices(businessInfo.id);
      setServices(servicesData || []);
      return servicesData;
    } catch (error) {
      console.error("Error refreshing services:", error);
      return null;
    }
  };

  // Function to refresh onboarding progress
  // Uses return value from refreshAccess() so callers get fresh data (state update is async)
  const refreshOnboardingProgress = async (salonId) => {
    try {
      const newAccess = await refreshAccess();
      if (newAccess?.onboarding) {
        return {
          initialOnboarding: newAccess.onboarding.initial_onboarding,
          progressiveEmployees: newAccess.onboarding.progressive_employees,
          progressiveServices: newAccess.onboarding.progressive_services,
          progressivePayments: newAccess.onboarding.progressive_payments,
        };
      }
      // Fallback to direct API call when access has no onboarding.
      // Without a salonId this hits the staff-keyed row, which with the
      // multi-location flag ON can belong to a DIFFERENT location — skip it.
      if (!salonId && multiLocationOn) {
        return null;
      }
      const progress = await getOnboardingProgress(salonId);
      setLocalOnboardingProgress(progress);
      return progress;
    } catch (error) {
      console.error("Error refreshing onboarding progress:", error);
      try {
        if (!salonId && multiLocationOn) {
          return null;
        }
        const progress = await getOnboardingProgress(salonId);
        setLocalOnboardingProgress(progress);
        return progress;
      } catch (fallbackError) {
        console.error("Fallback onboarding fetch also failed:", fallbackError);
        return null;
      }
    }
  };

  // Function to refresh all data after onboarding changes
  // Call this after initial onboarding completes or after progressive onboarding changes
  const refreshAllData = useCallback(async () => {
    try {
      setIsRoutingDataLoading(true);

      // Refresh access data (includes onboarding state). Keep the returned
      // value: the salon resolution below must use FRESH access (the state
      // update is async), and `access` in this closure may be stale.
      let refreshedAccess = access;
      try {
        refreshedAccess = (await refreshAccess()) ?? access;
      } catch (accessErr) {
        // Fallback to direct onboarding fetch — staff-keyed, so only legal
        // when the multi-location flag is OFF (see multiLocationOn above).
        if (!multiLocationOn) {
          try {
            const progress = await getOnboardingProgress();
            setLocalOnboardingProgress(progress);
          } catch (progressError) {
            // Silently handle missing onboarding progress
          }
        }
      }

      // Fetch salon info — resolved from /access when the flag is ON (never
      // the legacy salon_staff-linked salon; see boot effect above).
      let salon = null;
      try {
        salon = await fetchCurrentSalonRecord({
          access: refreshedAccess,
          getSalons,
          getSalonByUuid,
        });
        if (salon) {
          setBusinessInfo(salon);
          setIsMissingSalon(false);
        } else {
          setIsMissingSalon(true);
        }
      } catch (salonError) {
        setIsMissingSalon(true);
      }

      // If salon exists, fetch employees and services
      if (salon) {
        try {
          const fetchedStaff = await getSalonStaff(salon.id);
          setEmployees(fetchedStaff || []);
          // Update currentUser if we found the user in staff list
          if (fetchedStaff && currentCognitoUser) {
            const foundUser = fetchedStaff.find(
              (emp) => emp.id === currentCognitoUser.userId,
            );
            if (foundUser) {
              setCurrentUser(foundUser);
            }
          }
        } catch (err) {
          console.error("Error fetching staff:", err);
        }

        try {
          const servicesData = await getServices(salon.id);
          setServices(servicesData || []);
        } catch (err) {
          console.error("Error fetching services:", err);
        }
      }
    } catch (error) {
      console.error("Error in refreshAllData:", error);
    } finally {
      setIsRoutingDataLoading(false);
    }
  }, [
    access,
    multiLocationOn,
    refreshAccess,
    getOnboardingProgress,
    getSalons,
    getSalonByUuid,
    getSalonStaff,
    getServices,
    currentCognitoUser,
  ]);

  // Re-fetch just the CURRENT salon record (multi-location aware) and update
  // businessInfo. The canonical replacement for the old
  // `getSalons()[0] → setBusinessInfo` pattern, which resurrected the
  // pre-switch salon after a location switch (legacy salon_staff link).
  const refreshBusinessInfo = useCallback(async () => {
    // Never fetch before /access settles — the flag-ON resolution needs it,
    // and a premature legacy fetch would resurrect the pre-switch salon.
    if (!accessSettled) return null;
    try {
      // Read the LATEST access through the ref, not the closure: keeping the
      // `access` object in the deps re-minted this callback on every /access
      // refetch (fresh object each time), which re-fired consumers keyed on
      // its identity (e.g. a consumer's auto-fetch effect) — the exact
      // identity-churn amplifier the stable-facade work removed elsewhere.
      const salon = await fetchCurrentSalonRecord({
        access: accessRef.current,
        getSalons,
        getSalonByUuid,
      });
      if (salon) {
        setBusinessInfo(salon);
        setIsMissingSalon(false);
      }
      return salon;
    } catch (error) {
      console.error("Error refreshing business info:", error);
      return null;
    }
  }, [accessSettled, getSalons, getSalonByUuid]);

  // [LOOP] churn probe — identifies which field churns if this provider loops.
  // employees/currentUser were the max-depth culprits (empty-staff isSelf
  // loop) and were MISSING from the first probe — keep them covered.
  useValueChurnProbe("SalonContext", {
    businessInfo,
    access,
    isAccessLoading,
    accessError,
    employees,
    currentUser,
    services,
    onboardingProgress,
  });
  const value = {
    businessInfo,
    paymentTerminals,
    employees,
    services,
    isMissingSalon,
    onboardingStage,
    onboardingProgress,
    currentUser,
    serviceTypeOptions,
    isRoutingDataLoading,
    // Multi-location (PR A / #399)
    availableLocations,
    selectedSalonId,
    isLocationPickerOpen,
    setBusinessInfo,
    setEmployees,
    setServices,
    setOnboardingStage,
    setIsMissingSalon,
    setCurrentUser,
    setOnboardingProgress,
    setSelectedSalonId,
    openLocationPicker,
    closeLocationPicker,
    refreshOnboardingProgress,
    refreshBusinessInfo,
    refreshEmployees,
    refreshServices,
    refreshPaymentTerminals,
    refreshAllData,
  };

  return (
    <SalonContext.Provider value={value}>{children}</SalonContext.Provider>
  );
}

export const useSalon = () => {
  const context = useContext(SalonContext);
  if (!context) {
    throw new Error("useSalon must be used within a SalonProvider");
  }
  return context;
};
